<template>
	<v-card>
		<v-toolbar flat color="dark" dark>
			<v-toolbar-title>
				<span class="pl-2">Schoolyear Planning</span>
			</v-toolbar-title>
			<v-spacer></v-spacer>
			<v-btn text dark @click="legendOpen = true">
				<v-icon left>mdi-palette</v-icon>
				Kleuren codering
			</v-btn>
			<v-btn
				text
				dark
				:loading="reportLoading"
				:disabled="reportLoading || !filterSchoolyearId"
				@click="askPlanningReportAudience"
			>
				<v-icon left>mdi-file-pdf-box</v-icon>
				PDF rapport
			</v-btn>
		</v-toolbar>

		<v-card-title class="pb-0 pt-4 flex-wrap align-center">
			<v-select
				v-model="filterSchoolyearId"
				:items="schoolyearFilterItems"
				item-text="name"
				item-value="id"
				label="Schooljaar"
				dense
				filled
				hide-details
				class="mr-3 mb-2"
				style="max-width: 260px"
				clearable
			></v-select>
			<v-text-field
				v-model="search"
				append-icon="mdi-magnify"
				label="Zoeken"
				single-line
				hide-details
				dense
				class="flex-grow-1 mb-2"
				style="min-width: 200px"
			></v-text-field>
		</v-card-title>

		<v-alert v-if="loadError" type="error" dense outlined class="mx-4 mb-0">
			{{ loadError }}
		</v-alert>

		<v-data-table
			:headers="headers"
			:items="filteredData"
			:search="search"
			:loading="loading"
			class="elevation-0"
		>
			<template v-slot:top>
				<v-toolbar dark dense color="primary" flat class="mb-1">
					<v-btn depressed class="ml-2" color="inverted" @click="goNew">
						<v-icon class="pr-2">mdi-plus-circle</v-icon>
						Nieuwe activiteit
					</v-btn>
				</v-toolbar>
			</template>
			<template v-slot:item.action="{ item }">
				<v-icon color="primary" class="mr-2" style="cursor: pointer" @click="goEdit(item.id)">
					mdi-pencil
				</v-icon>
				<v-icon color="error" style="cursor: pointer" @click="confirmDelete(item)">
					mdi-delete
				</v-icon>
			</template>
			<template v-slot:item.color="{ item }">
				<span
					class="activity-swatch"
					:style="activitySwatchStyle(item.tbl_sys_schoolyear_planning_activity)"
					:title="activityName(item)"
				></span>
			</template>
			<template v-slot:item.activity="{ item }">
				{{ activityName(item) }}
			</template>
			<template v-slot:item.schoolyear="{ item }">
				{{ schoolyearName(item) }}
			</template>
			<template v-slot:item.start_date="{ item }">
				{{ formatDate(item.start_date) }}
			</template>
			<template v-slot:item.end_date="{ item }">
				{{ formatDate(item.end_date) }}
			</template>
			<template v-slot:item.time="{ item }">
				{{ formatTimeRange(item) }}
			</template>
			<template v-slot:item.activity_information="{ item }">
				<span class="activity-information-preview">
					{{ plainText(item.activity_information) }}
				</span>
			</template>
		</v-data-table>

		<v-dialog v-model="legendOpen" max-width="640">
			<v-card>
				<v-toolbar flat color="dark" dark dense>
					<v-toolbar-title>Kleuren codering</v-toolbar-title>
					<v-spacer></v-spacer>
					<v-btn icon dark @click="legendOpen = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-toolbar>
				<v-card-text class="pt-4">
					<v-simple-table dense>
						<tbody>
							<tr v-for="act in activities" :key="act.id">
								<td>{{ act.name }}</td>
								<td style="width: 120px">
									<span class="activity-swatch activity-swatch--wide" :style="activitySwatchStyle(act)"></span>
								</td>
							</tr>
						</tbody>
					</v-simple-table>
				</v-card-text>
			</v-card>
		</v-dialog>

		<v-dialog v-model="reportAudienceDialog" max-width="460">
			<v-card>
				<v-card-title class="headline">PDF rapport genereren</v-card-title>
				<v-card-text>
					<p class="mb-3">Voor wie is dit schooljaarplanning-rapport?</p>
					<v-radio-group v-model="reportAudience" mandatory>
						<v-radio label="Personeel" value="personnel"></v-radio>
						<v-radio label="Student" value="student"></v-radio>
					</v-radio-group>
					<p class="caption grey--text mb-0">
						Activiteiten worden meegenomen of weggelaten op basis van de
						instellingen Personeel/Student bij planning activities.
					</p>
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text @click="reportAudienceDialog = false">Annuleren</v-btn>
					<v-btn
						color="primary"
						depressed
						:loading="reportLoading"
						@click="openPlanningReport"
					>
						Genereer PDF
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog v-model="deleteDialog" max-width="420">
			<v-card>
				<v-card-title class="headline">Activiteit verwijderen?</v-card-title>
				<v-card-text>
					Weet u zeker dat u
					<strong>{{ deleteTargetLabel }}</strong>
					wilt verwijderen?
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text @click="deleteDialog = false">Annuleren</v-btn>
					<v-btn color="error" depressed :loading="deleting" @click="doDelete">Verwijderen</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-card>
</template>

<script>
import Axios from "axios";
import { API_ROOT } from "../../config";
import { activitySwatchStyle } from "../../constants/schoolyearPlanningActivities";
import { openPdf } from "../../services/reportService";

const ax = Axios.create({
	baseURL: API_ROOT["osbod_salary"],
	timeout: 60000
});

export default {
	name: "SchoolyearPlanning",
	data() {
		return {
			search: "",
			loading: false,
			loadError: "",
			data: [],
			activities: [],
			schoolyears: [],
			filterSchoolyearId: null,
			legendOpen: false,
			reportLoading: false,
			reportAudienceDialog: false,
			reportAudience: "personnel",
			deleteDialog: false,
			deleting: false,
			deleteTarget: null,
			headers: [
				{ text: "", align: "center", sortable: false, value: "action" },
				{ text: "", align: "center", sortable: false, value: "color" },
				{ text: "Activiteit", value: "activity" },
				{ text: "Schooljaar", value: "schoolyear" },
				{ text: "Startdatum", value: "start_date" },
				{ text: "Einddatum", value: "end_date" },
				{ text: "Tijd", value: "time", sortable: false },
				{ text: "Locatie", value: "location" },
				{
					text: "Activiteitsinformatie",
					value: "activity_information",
					sortable: false
				},
				{ text: "Opmerking", value: "remark" }
			]
		};
	},
	computed: {
		schoolyearFilterItems() {
			return this.schoolyears || [];
		},
		filteredData() {
			if (this.filterSchoolyearId == null || this.filterSchoolyearId === "") {
				return this.data;
			}
			var sid = Number(this.filterSchoolyearId);
			return (this.data || []).filter(function(row) {
				return Number(row.schoolyear_id) === sid;
			});
		},
		deleteTargetLabel() {
			if (!this.deleteTarget) return "";
			return (
				this.activityName(this.deleteTarget) +
				" (" +
				this.formatDate(this.deleteTarget.start_date) +
				" – " +
				this.formatDate(this.deleteTarget.end_date) +
				")"
			);
		}
	},
	created() {
		this.fetchLookups();
		this.fetchItems();
	},
	methods: {
		activitySwatchStyle: activitySwatchStyle,
		activityName(item) {
			return (
				(item &&
					item.tbl_sys_schoolyear_planning_activity &&
					item.tbl_sys_schoolyear_planning_activity.name) ||
				""
			);
		},
		schoolyearName(item) {
			return (item && item.tbl_sys_schoolyear && item.tbl_sys_schoolyear.name) || "";
		},
		formatDate(value) {
			if (!value) return "";
			var s = String(value);
			if (s.length >= 10) return s.slice(0, 10);
			return s;
		},
		formatTime(value) {
			if (!value) return "";
			return String(value).slice(0, 5);
		},
		formatTimeRange(item) {
			var start = this.formatTime(item && item.start_time);
			var end = this.formatTime(item && item.end_time);
			if (start && end) return start + " – " + end;
			return start || end || "";
		},
		plainText(html) {
			if (!html) return "";
			var element = document.createElement("div");
			element.innerHTML = String(html);
			return (element.textContent || element.innerText || "").trim();
		},
		askPlanningReportAudience() {
			if (!this.filterSchoolyearId) {
				this.loadError = "Selecteer een schooljaar om het PDF-rapport te genereren.";
				return;
			}
			this.loadError = "";
			this.reportAudience = this.reportAudience || "personnel";
			this.reportAudienceDialog = true;
		},
		async openPlanningReport() {
			var user = null;
			try {
				user = JSON.parse(localStorage.getItem("user") || "null");
			} catch (e) {
				user = null;
			}
			if (!user || !user.id || !this.filterSchoolyearId) {
				this.loadError = "Selecteer een schooljaar om het PDF-rapport te genereren.";
				return;
			}
			if (this.reportAudience !== "personnel" && this.reportAudience !== "student") {
				this.loadError = "Kies Personeel of Student voor het rapport.";
				return;
			}
			this.reportLoading = true;
			this.loadError = "";
			try {
				await openPdf("schoolyear-planning", {
					user_id: user.id,
					schoolyearId: this.filterSchoolyearId,
					audience: this.reportAudience
				});
				this.reportAudienceDialog = false;
			} catch (e) {
				this.loadError =
					(e && e.message) ||
					"Kon het schooljaarplanning-rapport niet genereren.";
			} finally {
				this.reportLoading = false;
			}
		},
		goNew() {
			var q = {};
			if (this.filterSchoolyearId) {
				q.schoolyear_id = String(this.filterSchoolyearId);
			}
			this.$router.push({ path: "/schooladmin/schoolyear_planning/new", query: q });
		},
		goEdit(id) {
			this.$router.push({ path: "/schooladmin/schoolyear_planning/update/" + id });
		},
		confirmDelete(item) {
			this.deleteTarget = item;
			this.deleteDialog = true;
		},
		doDelete() {
			var self = this;
			if (!self.deleteTarget || self.deleteTarget.id == null) return;
			self.deleting = true;
			ax.post("/schoolyear_planning/delete/" + self.deleteTarget.id)
				.then(function() {
					self.deleteDialog = false;
					self.deleteTarget = null;
					self.fetchItems();
				})
				.catch(function() {
					self.loadError = "Verwijderen mislukt.";
				})
				.finally(function() {
					self.deleting = false;
				});
		},
		fetchLookups() {
			var self = this;
			ax.get("/schoolyear_planning/activities")
				.then(function(res) {
					self.activities = Array.isArray(res.data) ? res.data : [];
				})
				.catch(function() {
					self.activities = [];
				});
			ax.get("/schoolyear/all")
				.then(function(res) {
					self.schoolyears = Array.isArray(res.data) ? res.data : [];
					if (
						self.filterSchoolyearId == null &&
						self.schoolyears.length > 0
					) {
						self.filterSchoolyearId = self.schoolyears[0].id;
					}
				})
				.catch(function() {
					self.schoolyears = [];
				});
		},
		fetchItems() {
			var self = this;
			self.loading = true;
			self.loadError = "";
			ax.get("/schoolyear_planning/all")
				.then(function(res) {
					self.data = Array.isArray(res.data) ? res.data : [];
				})
				.catch(function(err) {
					var msg =
						(err &&
							err.response &&
							err.response.data &&
							err.response.data.msg) ||
						"Planning records konden niet worden geladen.";
					self.loadError = msg;
					self.data = [];
				})
				.finally(function() {
					self.loading = false;
				});
		}
	}
};
</script>

<style scoped>
.activity-swatch {
	display: inline-block;
	width: 28px;
	height: 18px;
	border-radius: 2px;
	vertical-align: middle;
}
.activity-swatch--wide {
	width: 72px;
	height: 22px;
}
.activity-information-preview {
	display: inline-block;
	max-width: 280px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	vertical-align: middle;
}
</style>
